import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define interfaces
export interface NotificationPreference {
  id: string;
  user: string;
  notification_type: string;
  channels: {
    email: boolean;
    in_app: boolean;
    sms?: boolean;
    push?: boolean;
  };
  frequency: 'immediate' | 'daily' | 'weekly';
  enabled?: boolean;
}

export interface Notification {
  id: string;
  user: string;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  read: boolean;
  related_entity_type?: string;
  related_entity_id?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

// Create API
export const notificationApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: createExtendedTokenBaseQuery(""),
  tagTypes: ['Notification', 'NotificationPreference'],
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationPaginatedResponse,
      { page?: number; limit?: number; unread_only?: boolean; notification_type?: string }
    >({
      query: (params = { page: 1, limit: 10, unread_only: false }) => {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());
        if (params.notification_type) queryParams.append('notification_type', params.notification_type);
        
        return {
          url: params.unread_only ? 'notifications/api/unread/' : 'notifications/api/',
          params: queryParams,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Notification' as const, id })),
              { type: 'Notification' as const, id: 'LIST' },
            ]
          : [{ type: 'Notification' as const, id: 'LIST' }],
    }),
    
    getNotificationCount: builder.query<
      {
        count: number;
        type_breakdown: Record<string, number>
      },
      { notification_type?: string }
    >({
      // Disable the API call by returning a mock response
      queryFn: () => {
        return {
          data: {
            count: 0,
            type_breakdown: {}
          }
        };
      },
      providesTags: [{ type: 'Notification' as const, id: 'COUNT' }],
    }),
    
    markNotificationAsRead: builder.mutation<void, string>({
      query: (id) => ({
        url: `notifications/api/${id}/read/`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Notification' as const, id },
        { type: 'Notification' as const, id: 'COUNT' },
      ],
    }),
    
    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({
        url: 'notifications/api/read-all/',
        method: 'PUT',
      }),
      invalidatesTags: [
        { type: 'Notification' as const, id: 'LIST' },
        { type: 'Notification' as const, id: 'COUNT' },
      ],
    }),
    
    getNotificationPreferences: builder.query<NotificationPreference[], void>({
      query: () => 'notifications/api/preferences/',
      providesTags: [{ type: 'NotificationPreference' as const, id: 'LIST' }],
    }),
    
    getNotificationPreferenceByType: builder.query<NotificationPreference, string>({
      query: () => 'notifications/api/preferences/',
      transformResponse: (response: NotificationPreference[], meta, arg: string) => {
        // Find the preference with the matching notification_type
        const preference = response.find(pref => pref.notification_type === arg);
        if (!preference) {
          // Return a default preference if not found
          return {
            id: '',
            user: '',
            notification_type: arg,
            channels: {
              in_app: true,
              email: true,
              sms: false,
              push: false
            },
            frequency: 'immediate',
            enabled: true
          };
        }
        return preference;
      },
      providesTags: (result, error, arg) => [{ type: 'NotificationPreference' as const, id: arg }],
    }),
    
    updateNotificationPreferences: builder.mutation<void, NotificationPreference>({
      query: (preferences) => ({
        url: 'notifications/api/preferences/',
        method: 'PUT',
        body: [preferences],
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'NotificationPreference' as const, id: 'LIST' },
        { type: 'NotificationPreference' as const, id: arg.notification_type },
      ],
    }),
    
    testNotification: builder.mutation<void, { notification_type: string; channel: string }>({
      query: (params) => ({
        url: 'notifications/api/test/',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: [],
    }),
  }),
});

// Export hooks
export const {
  useGetNotificationsQuery,
  useGetNotificationCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetNotificationPreferencesQuery,
  useGetNotificationPreferenceByTypeQuery,
  useUpdateNotificationPreferencesMutation,
  useTestNotificationMutation,
} = notificationApi;